import React, { useState } from 'react';
import { ReportInfoPanel } from '../../shared/components/ReportInfoPanel';
import ApolloError from '../../shared/components/ApolloError';
import ActualPerformanceGraph from '../../shared/components/ActualPerformanceGraph';
import { APPRAISAL_SPONSOR } from '../../../constants/appraisal-team-member-types';
import UnitDetailsBox from '../../shared/components/UnitDetailsBox';
import { get } from 'lodash';
import { isUserFDATier } from '../../shared/services/checkUserTierService';
import PageInstructions from '../../shared/components/PageInstructions';
import DataPointInfoDetails from './DataPointInfoDetails';
import usePerformanceOrgUnitQuery from '../hooks/usePerformanceOrgUnitQuery';
import PerformanceItemInfoDetails from './PerformanceItemInfoDetails';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';
import GET_ORG_UNITS_SHARED_REPORTS_WITH_FDA from '../queries/getOrgUnitSharedReportsWithFdaQuery';
import { useQuery } from '@apollo/client';
import NotEnoughDataMessage from '../../shared/components/NotEnoughDataMessage';
import { toHumanReadableDate } from '../../shared/services/dateService';
import PerformanceItemMapper from '../mappers/performanceItemMapper';
import { INFO_BOX_TYPES } from '../constants/infoBoxTypes';

/**
 * @typedef {object} ActualPerformanceReportProps
 * @property {number} orgUnitId
 * @property {string} [tier]
 */

/**
 * @param {ActualPerformanceReportProps} props
 */
const ActualPerformanceReport = props => {
  const { orgUnitId, tier } = props;

  const [infoBoxType, setInfoBoxType] = useState(INFO_BOX_TYPES.DEFAULT);
  const [infoBoxData, setInfoBoxData] = useState(null);

  const { orgUnitPerformanceData, error, loading } = usePerformanceOrgUnitQuery(orgUnitId, tier);

  const {
    data: sharedUnitsWithFda,
    error: sharedUnitsWithFdaError,
    loading: sharedUnitsWithFdaLoading
  } = useQuery(GET_ORG_UNITS_SHARED_REPORTS_WITH_FDA, {
    variables: { orgUnitId: orgUnitId },
    skip: isUserFDATier(tier)
  });

  const handleClickOutside = () => {
    setInfoBoxType(INFO_BOX_TYPES.DEFAULT);
    setInfoBoxData(null);
  };

  const handleDateClick = (element, performanceItem) => {
    const dataPoint = performanceItem.DataPoints.find(x => x.Date === element);

    setInfoBoxType(INFO_BOX_TYPES.DATA_POINT);
    setInfoBoxData({
      dataPoint,
      measurementName: performanceItem.MeasurementName
    });
  };

  const handleTitleClick = performanceItem => {
    setInfoBoxType(INFO_BOX_TYPES.PERFORMANCE_ITEM);
    setInfoBoxData({ performanceItem });
  };

  const renderInfoBox = () => {
    switch (infoBoxType) {
      case INFO_BOX_TYPES.DATA_POINT:
        return (
          <DataPointInfoDetails
            dataPoint={infoBoxData.dataPoint}
            measurementName={infoBoxData.measurementName}
          />
        );
      case INFO_BOX_TYPES.PERFORMANCE_ITEM:
        return <PerformanceItemInfoDetails performanceItem={infoBoxData.performanceItem} />;
      case INFO_BOX_TYPES.DEFAULT:
      default:
        return (
          <span>Select a metric (graph title) or check point date (x-axis) for more info.</span>
        );
    }
  };

  const renderReportGraphs = () => {
    if (loading) {
      return (
        <div className='actual-report__graph'>
          <SkeletonWrapper show={true} inheritParentSize inheritChildHeight />
        </div>
      );
    }

    const performanceItemGraphEntries = orgUnitPerformanceData.PerformanceItems.map(
      mapPerformanceItemToGraphEntry
    );

    if (!performanceItemGraphEntries.length) {
      return (
        <div className='actual-report__graph'>
          <NotEnoughDataMessage />
        </div>
      );
    }

    return performanceItemGraphEntries.map(pi => (
      <ActualPerformanceGraph
        key={pi.Id}
        performanceItem={pi}
        dateClick={handleDateClick}
        titleClick={handleTitleClick}
        sharedWithFda={getIsSharedWithFda(
          sharedUnitsWithFda,
          sharedUnitsWithFdaLoading,
          pi.Id,
          tier
        )}
      />
    ));
  };

  const renderUnitDetailsBox = () => {
    if (Object.keys(orgUnitPerformanceData).length > 0) {
      const orgUnit = orgUnitPerformanceData;
      const appraisalOrgUnits = get(orgUnit, 'AppraisalOrgUnits', []);
      const latestAppraisal = appraisalOrgUnits[0].Appraisal;
      const address = [
        orgUnit.Address1,
        orgUnit.Address2,
        orgUnit.City,
        orgUnit.State,
        orgUnit.ZipCode
      ]
        .filter(Boolean)
        .join(', ');

      const OHTs = orgUnit.MddapOfficeOfHealthTechnologies.map(x =>
        x.OfficeOfHealthTechnology.Value.split(':')[0].replace('OHT', '')
      ).join(', ');
      const enrollmentDate = toHumanReadableDate(orgUnit.EnrollmentDate);
      const appraisalTeamMembers = latestAppraisal.AppraisalTeamMembers;
      const sponsorName = appraisalTeamMembers.find(x => x.Type === APPRAISAL_SPONSOR).Individual
        .Name;

      return (
        <UnitDetailsBox
          data={{
            location: address,
            FEI: orgUnit.Fei,
            enrollmentDate: enrollmentDate,
            OHTs: OHTs,
            sponsorName: sponsorName
          }}
          showAppraisalDetails={false}
        />
      );
    }
  };

  const description =
    'Performance Report metrics represented graphically over all time, ' +
    'including actuals and limits. Select a metric (graph title) or check point date (x-axis) for more info.';

  if (error || sharedUnitsWithFdaError) {
    return <ApolloError />;
  }

  return (
    <div onClick={handleClickOutside}>
      <PageInstructions title='Actual Performance Report' description={description} />
      {!loading && !orgUnitPerformanceData?.AppraisalOrgUnits.length ? (
        <div className='actual-report__graph'>
          <NotEnoughDataMessage />
        </div>
      ) : (
        <div className='report__body'>
          <div className='report-info-panels'>
            <ReportInfoPanel
              panelName='report-info-panel__small'
              loading={loading}
              renderChildren={renderUnitDetailsBox}
            />
            <ReportInfoPanel panelName='report-info-panel__more-info report-info-panel__large'>
              {renderInfoBox()}
            </ReportInfoPanel>
          </div>
          <div className='actual-report__graphs'>{renderReportGraphs()}</div>
        </div>
      )}
    </div>
  );
};

const mapPerformanceItemToGraphEntry = performanceItem => ({
  ...PerformanceItemMapper.performanceItemToGraphData(performanceItem),
  DataPoints: performanceItem.CheckpointPerformanceItemDataPoints.map(mapDataPointToGraphEntry)
});

const mapDataPointToGraphEntry = dataPoint => ({
  ...dataPoint,
  Date: dataPoint.Checkpoints.DataCollectionDate,
  ReporterName: dataPoint.Checkpoints.ReporterName
});

const getIsSharedWithFda = (
  sharedUnitsWithFda,
  sharedUnitsWithFdaLoading,
  performanceItemId,
  tier
) => {
  if (isUserFDATier(tier)) {
    return false;
  }

  if (
    sharedUnitsWithFdaLoading ||
    !sharedUnitsWithFda ||
    !sharedUnitsWithFda.mddapOrgUnitFdaSharing
  ) {
    return false;
  }

  const sharedPerformanceReports =
    sharedUnitsWithFda.mddapOrgUnitFdaSharing.SharedPerformanceReports;

  if (!sharedPerformanceReports) {
    return false;
  }

  return sharedPerformanceReports.some(
    sharedPerformanceReport =>
      sharedPerformanceReport.MddapCheckpointPerformanceItemId === performanceItemId ||
      sharedPerformanceReport.MddapCheckpointPerformanceItemId === null
  );
};

export default ActualPerformanceReport;
